import { Events } from '@APP/events/app-events';
import { DepInjMixin, requires } from '@APP/mixins/dependency';
import { ConfigService } from '@APP/services/config-service';
import { UserService } from '@APP/services/user-service';
import { eventBus } from '@APP/utils/event-bus';
import { t } from '@APP/utils/locale';
import { PopupData } from '@FLAVOR/../../common/types';
import { css, CSSResultGroup, html, svg } from 'lit';
import { customElement, property } from 'lit/decorators';
import { DeviceAwareComponent } from '../components/device-aware-component';
import { Document, LogoutIcon, StartoverIcon, PolicyIcon, UserIcon } from '../icons/icons';

const Decoration = svg`
<svg id="decoration" width="324" height="120" viewBox="0 0 324 120" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M84.9036 103.032C43.4241 100.508 -1.04532e-05 119.571 -1.04532e-05 119.571L0 0L324 1.48656L324 119.571C324 119.571 291.785 87.7317 258.614 90.028C230.575 91.9691 208.456 116.259 180.542 119.571C151.646 123 113.945 104.799 84.9036 103.032Z" fill="url(#paint0_linear_6941_120157)"/>
<defs>
<linearGradient id="paint0_linear_6941_120157" x1="162" y1="7.00219" x2="162" y2="120.037" gradientUnits="userSpaceOnUse">
<stop stop-color="#744BDA"/>
<stop offset="1" stop-color="#7F56E3" stop-opacity="0.56"/>
</linearGradient>
</defs>
</svg>
`;

type OwnDeps = {
  configService: ConfigService;
  userService: UserService;
};

@requires('configService', 'userService')
@customElement('app-menu')
export class AppMenu extends DepInjMixin<OwnDeps>(DeviceAwareComponent) {

  @property({ type: Boolean, attribute: 'open', reflect: true })
    isOpen = false;


  private subs: Function[] = [];

  static get mobileStyles(): CSSResultGroup {
    return [
      css`
    #toggle {
      background-image: url(./assets/hamburger.svg);
      padding: 0;
      margin: 0;
      width: 6rem;
      height: 6rem;
      background-size: auto;
      background-repeat: no-repeat;
      background-color: var(--btn-active-bg);
      outline: none;
      border: none;
      background-position: 50% 50%;
      position: absolute;
      top: 0;
      left: 0;
      transition: 450ms var(--transition-switch);
      transition-property: top, left, text-indent, transform, background-position;
      transition-delay: 0ms, 0ms, 0ms, 0ms;
      border-radius: 50%;
      overflow: hidden;
      text-indent: 6rem;
      font-size: 3rem;
      color: var(--btn-pri-bg);
      font-weight: 100;
      font-stretch: expanded;
      transform: rotate(0deg);
    }

    #content {
      width: 40.5rem;
      height: 100vh;
      background: linear-gradient(180deg, #744BDA 55%, rgb(127, 86, 227) 100%);
      position: absolute;
      top: -100vh;
      transition: 350ms var(--transition-switch);
      display: none;
      justify-content: space-around;
      grid-template-rows: 18vh auto;
      align-items: stretch;
    }
      
      #content .menu {
        margin-top: 10rem;
      }

    #content .menu-item {
      color: var(--btn-active-bg);
      font-size: 2.5rem;
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 2rem;
      width: 32rem;
      height: 8rem;
      background: transparent;
      border: none;
      padding: 0;
    }

    .menu-item > svg {
      width: 2.5rem;
      height: auto;
    }

    #content #logout {
      border-top: 1px dashed var(--btn-disabled-color);
      margin-top: 15rem;
    }

    #decoration {
      position: absolute;
      top: 10rem;
      left: 0;
      width: 40.5rem;
      height: auto;
    }

    #user-info {
      flex-grow: 1;
      display: flex;
      align-items: center;
      color: var(--btn-active-bg);
      font-size: 3rem;
      z-index: 1;
      width: 32rem;
      margin-top: 12rem;
    }
    .user-icon {
      width: 11.5rem;
      height: 8.5rem;
    }
    :host([open]) #content { display:grid; top: 0; }
    :host([open]) #toggle {
      background-color: var(--btn-pri-bg);
      color: var(--btn-active-bg);
      top: 2rem;
      left: calc(100vw - 13.5rem);
      transition-delay: 0ms, 250ms, 250ms, 250ms;
      text-indent: 0;
      background-position: -16rem 50%;
    }
    `,
    ];
  };

  connectedCallback() {
    super.connectedCallback();
    this.subs.push(eventBus().on<string>(Events.Type.PopupClose, popupName => {
      const desktop = this.userDevice === 'desktop';
      /** Closing menu on Mobile */
      if (popupName === 'before-exit-popup' && !desktop) {
        this.isOpen = false;
      }
    }));
  }

  disconnectedCallback(): void {
    this.subs.forEach(fn => fn());

  }

  toggleMenu() {
    this.isOpen = !this.isOpen;
  }

  showBeforeExitPopup() {
    const popupData: PopupData = {
      popupName: 'before-exit-popup',
      message: '',
      buttonText: 'Logout',
      popupClassName: 'before-exit-popup',
    };

    eventBus().emit(Events.Type.ShowPopup, popupData);
  }

  renderContent() {
    return html`
    <div id="content">
      ${Decoration}
      
      <div id="user-info">
      <span class="user-icon">${UserIcon}</span> Ahoy, ${this.deps.userService.getNameTitle()}!
      </div>
      <div class="menu" >
      <a class="menu-item" target="_self" href="${(this.deps.configService.get('privacyUrl') || '')}">
      ${PolicyIcon}  ${t('Privacy Policy')}
      </a>
      <a class="menu-item" target="_self" href=${(this.deps.configService.get('termsUrl') || '')}> 
      ${Document} ${t('Terms & Conditions')}</a>
<!--      <button class="menu-item" @click=${() => this.deps.userService.reStart()}>${StartoverIcon} Start over</button>-->
     </div>
      <span id="logout" class="menu-item">
        <button class="menu-item" @click=${this.showBeforeExitPopup}> ${LogoutIcon} Logout</button>
      </span>
    </div>
    <button id="toggle" @click=${this.toggleMenu}>✕</button>
    `;
  }
}